import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Color,
  defined,
  EllipsoidTerrainProvider,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";
import AnalysisVisibility from "../analysis/AnalysisVisibility";
import PopupAnalysisVisibility from "../popup/PopupAnalysisVisibility";
import Utils from "../basic/Utils";

/**
 * 가시성 분석을 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input (mouse/keyboard) class for visibility analysis.
 * @alias InteractionVisibility
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {AnalysisVisibility} [analysisVisibility=undefined] 가시성 분석 연산 및 영역 표시 클래스. <br> Visibility analysis operation and area display class.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * // 1. 생성 시 AnalysisVisibility 할당
 * const analysisVisibility = new Ditap.AnalysisVisibility(viewer);
 * const interactionVisibility = new Ditap.InteractionVisibility(viewer, analysisVisibility);
 * interactionVisibility.on();
 *
 * // 2. 생성 후 AnalysisVisibility 할당
 * const analysisVisibility = new Ditap.AnalysisVisibility(viewer);
 * const interactionVisibility = new Ditap.InteractionVisibility(viewer);
 * interactionVisibility.analysisVisibility = analysisVisibility;
 * interactionVisibility.on();
 *
 */
class InteractionVisibility {
  constructor(viewer, analysisVisibility) {
    this._handler = undefined; // 핸들러
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    } else {
      throw Error("InteractionVisibility viewer type error");
    }

    this._analysisVisibility = undefined;
    if (analysisVisibility instanceof AnalysisVisibility) {
      this._analysisVisibility = analysisVisibility;
    } else if (analysisVisibility === undefined) {
      this._analysisVisibility = undefined;
    } else {
      throw Error("InteractionVisibility analysisVisibility type error");
    }

    this._mousePosition = undefined;

    this._isOn = false; // 핸들러 실행 여부

    this._keyDownEvent = undefined;
    this._infoLabelEntity = undefined;
    this._buttonElement = undefined;
    this._popup = true;
    this.cancelBtn = undefined;
    this._isMobile = undefined; // 모바일 여부
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  get mousePosition() {
    return this._mousePosition;
  }
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화 상태.</br>
   * Handler enabled or disabled.
   * @readonly
   * @type {boolean}
   */
  get isOn() {
    return this._isOn;
  }
  // set isOn(isOn) {
  //   this._isOn = isOn;
  // }

  /**
   * 가시성 분석 연산 및 영역 표시 클래스.<br>
   * Visibility analysis operation and area display class.
   * @type {AnalysisVisibility | undefined}
   */
  get analysisVisibility() {
    return this._analysisVisibility;
  }
  set analysisVisibility(analysisVisibility) {
    this._analysisVisibility = analysisVisibility;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /*
   * 가시성 분석 후 기본 팝업 표시 여부.
   * Whether to display the default popup after visibility analysis.
   * @type {boolean}
   */
  get popup() {
    return this._popup;
  }
  set popup(popup) {
    this._popup = popup;
  }

  /*
   * Ditap Viewer에 pointEntity 생성.</br>
   * Create a pointEntity in the DitapViewer.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {string} id 포인트 엔티티 아이디.<br> Point id
   * @param {Cartesian3} position Point Entity 생성 좌표. </br> cartesian of pointEntity.
   * @param {Property | Color} outlineColor 점 강조 속성.</br> Properties that specify the material used to outline the points.
   * @returns {Entity} 생성된 Point Entity. </br> Created pointEntity.
   */
  createPoint(viewer, id, position, color = Color.WHITE) {
    const point = viewer.entities.add({
      id: id || undefined,
      position: position,
      point: {
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        color: Color.WHITE,
        pixelSize: 8,
        outlineColor: color,
        outlineWidth: 2,
      },
    });

    return point;
  }

  /*
   * Terrain provider 여부에 따라 새로운 Cartesian 반환.</br>
   * Returns new cartesian based on ellipsoid terrain provider presence.
   * @param {Cartesian3} position 재구성할 Cartesian 좌표.</br> The cartesian to be reconstructed.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Cartesian3} 재구성된 Cartesian 좌표. </br> Reconstructed based on ellipsoid terrain provider presence.
   */
  pickPosition(position, viewer) {
    let newPosition;
    let isEllipsoid = false;
    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      newPosition = viewer.scene.pickPosition(position);
    } else {
      // newPosition = viewer.camera.pickEllipsoid(
      //   position,
      //   viewer.scene.globe.ellipsoid
      // );
      const picked = viewer.scene.pick(position);
      if (defined(picked)) {
        newPosition = viewer.scene.pickPosition(position);
      } else {
        newPosition = viewer.camera.pickEllipsoid(
          position,
          viewer.scene.globe.ellipsoid
        );
      }
    }

    return newPosition;
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (!(this.analysisVisibility instanceof AnalysisVisibility)) {
        throw Error(
          "InteractionVisibility this.analysisVisibility is not defined"
        );
      }
      const viewer = this.viewer;
      // 마우스 좌표 설정
      const position = this.pickPosition(e.position, viewer);
      this.mousePosition = position;

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      if (defined(position)) {
        const options = {
          position: this.mousePosition,
          callback: () => {
            if (this.popup) {
              const popup =
                viewer.ditapStorage.visibilityPopups[
                  this.analysisVisibility.id
                ];
              if (popup) {
                popup.open();
              } else {
                const popupAnalysisVisibility = new PopupAnalysisVisibility(
                  this.analysisVisibility
                );
                viewer.ditapStorage.visibilityPopups[
                  this.analysisVisibility.id
                ] = popupAnalysisVisibility;
                popupAnalysisVisibility.open();
              }
            }
          },
        };

        this.analysisVisibility.createVisibility(options);

        this.off();
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      // 마우스 좌표 설정
      const position = this.pickPosition(e.endPosition, viewer);
      if (defined(position)) {
        this.mousePosition = position;

        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.off();
      this.abort();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    if (!this.analysisVisibility?.isReady) {
      this.analysisVisibility?.destroy();
    }

    this.analysisVisibility = undefined;
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  // * @param {function} callback if you a create visiblity entity, return a visibility resolution instance.
  // * @example
  // * interactionVisibility.on(
  // *  (analysisVisibility) => {}
  // * );

  /**
   * 가시성 분석 핸들러 실행. (AnalysisVisibility 초기화 필수.)</br>
   * Execute visibility analysis handler. (AnalysisVisibility initialization is required.)
   */
  on() {
    if (!this._isOn) {
      this.off();
      this.viewer.currentEvent = this;
      this._isMobile = Utils.detectDeviceType();

      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      const viewer = this.viewer;

      // 핸들러 상태 변경
      this._isOn = true;

      this.handler = new ScreenSpaceEventHandler(viewer.canvas); // 핸들러

      // 더블 클릭 시 시점 고정 제거
      // viewer.screenSpaceEventHandler.removeInputAction(
      //   ScreenSpaceEventType.LEFT_DOUBLE_CLICK
      // );

      // 라벨, 마우스 guid
      // const guid = createGuid();
      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여",
        "가시성 분석 시작점을 선택해주세요"
      );
      // 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 가시성 분석 시작점을",
        "선택해주세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 실행
      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 가시성 분석 핸들러 종료. (AnalysisVisibility 초기화 필수.) </br>
   * End visibility analysis handler. (AnalysisVisibility initialization is required.)
   */
  off() {
    if (this._isOn) {
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      if (!this.handler?.isDestroyed()) {
        this.handler?.destroy();
      }
      this.handler = undefined;

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      window.removeEventListener("keydown", this.keyDownEvent);
      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      // 변수 초기화
      this._isOn = false;
      this._isMobile = undefined;
      this.viewer.currentEvent = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
      this.infoLabelEntity = undefined;
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    // ESC : 체적 측정 실행 취소
    if (event.key === "Escape") {
      this.off();
      this.abort();
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}

export default InteractionVisibility;
